import { Paths } from "@app/routes";
import { ConfirmAction } from "@atlas/composite";
import {
  Badge,
  Button,
  ButtonGroup,
  CardDescription,
  cn,
  FieldContent,
  Form,
  Label,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { HubPackageStatus, IHubPackageVisibility } from "@enums";
import type { IBadgeStripItem, IHubCatalog, IHubPackage } from "@models";
import {
  BadgeStrip,
  CheckoutCartOrderedFormLine,
  checkoutCartLineKey,
  findHubCatalogForPackage,
  getOrderedCartEntries,
  isAddonCatalog,
  isSolutionCatalog,
} from "@modules";
import { useSearchHubCatalogs, useUpdateHubPackage } from "@query";
import { useRemoveFromCart } from "@shared/hooks";
import {
  catalogRelationToCheckoutRow,
  isCatalogVisibilityAllowedInPackage,
  plainCatalogDescription,
  resolveHubCatalogTypeBadge,
  resolveHubCatalogVisibilityBadge,
} from "@utils";
import { PackageX, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

function createCheckoutCartSchema(
  rows: IHubPackage[],
  catalogs: IHubCatalog[],
  currentPackage: IHubPackage | undefined,
  t: (
    key: string,
    options?: { defaultValue?: string; label?: string },
  ) => string,
) {
  return z
    .object({
      lineIds: z.array(z.string()).optional(),
    })
    .superRefine((_, ctx) => {
      const unknownCatalogMsg = t("hub.checkout.cart_unknown_catalog", {
        defaultValue:
          "This item could not be matched to a catalog. Remove it or refresh the catalog list.",
      });
      const onlyOneSolutionMsg = t("hub.checkout.cart_only_one_solution", {
        defaultValue:
          "Your package can only include one solution. Remove extra solutions from the package.",
      });
      const visibilityMismatchMsg = t("hub.checkout.cart_visibility_mismatch", {
        defaultValue:
          "This item's visibility does not match the package visibility. Please remove it from your package.",
      });

      const matched = rows.map((pkg, rowIndex) => ({
        pkg,
        rowIndex,
        catalog: findHubCatalogForPackage(catalogs, pkg),
      }));

      for (const m of matched) {
        if (!m.catalog) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: unknownCatalogMsg,
            path: ["lineIds", m.rowIndex],
          });
        } else if (
          currentPackage?.visibility &&
          m.catalog.visibility &&
          !isCatalogVisibilityAllowedInPackage(
            m.catalog.visibility,
            currentPackage.visibility,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: visibilityMismatchMsg,
            path: ["lineIds", m.rowIndex],
          });
        }
      }

      const withCatalog = matched.filter(
        (m): m is (typeof matched)[number] & { catalog: IHubCatalog } =>
          m.catalog != null,
      );
      const solutions = withCatalog.filter((m) => isSolutionCatalog(m.catalog));

      if (solutions.length > 1) {
        for (const m of solutions) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: onlyOneSolutionMsg,
            path: ["lineIds", m.rowIndex],
          });
        }
      }
    });
}

type CheckoutCartFormValues = z.infer<
  ReturnType<typeof createCheckoutCartSchema>
>;

type RemoveConfirmState =
  | { mode: "line"; pkg: IHubPackage }
  | { mode: "bundle"; solutionPkg: IHubPackage }
  | null;

function catalogRelationKey(r: {
  catalogId?: number;
  catalogVersion?: number;
}): string {
  return `${r.catalogId ?? ""}:${r.catalogVersion ?? ""}`;
}

export interface ICheckoutPackageSummaryDetails {
  titleLabel: string;
  createdAtIso: string | null;
}

interface ICheckoutCatalogsProps {
  formId?: string;
  catalogs: { catalogId?: number; catalogVersion?: number }[];
  currentPackage?: IHubPackage;
  isReadOnly?: boolean;
  isPopover?: boolean;
  onSubmitSuccess?: () => void;
}

export function CheckoutCatalogs({
  formId,
  catalogs,
  currentPackage,
  isReadOnly = false,
  isPopover = false,
  onSubmitSuccess,
}: ICheckoutCatalogsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, isLoading: catalogsLoading } = useSearchHubCatalogs({});
  const catalogsData = useMemo(
    () =>
      data?.pages.flatMap((page: { items: IHubCatalog[] }) => page.items) ?? [],
    [data],
  );

  const rows = useMemo(() => {
    return catalogs.map((rel) => {
      const catalog = catalogsData.find(
        (c) =>
          c.catalogId === rel.catalogId && c.version === rel.catalogVersion,
      );
      return catalogRelationToCheckoutRow(
        {
          packageId: currentPackage?.packageId ?? 0,
          status: currentPackage?.status ?? HubPackageStatus.Ready,
          visibility:
            currentPackage?.visibility ?? IHubPackageVisibility.Private,
          name: "",
          label: "",
          description: "",
        },
        {
          catalogId: rel.catalogId ?? 0,
          catalogVersion: rel.catalogVersion ?? 0,
        },
        catalog,
      );
    });
  }, [catalogs, catalogsData, currentPackage]);

  const { mutate: updateHubPackage } = useUpdateHubPackage();
  const { removeFromCart } = useRemoveFromCart();
  const [removeConfirm, setRemoveConfirm] = useState<RemoveConfirmState>(null);

  const validationSchema = useMemo(
    () => createCheckoutCartSchema(rows, catalogsData, currentPackage, t),
    [rows, catalogsData, currentPackage, t],
  );

  const cartFormValues = useMemo(
    (): CheckoutCartFormValues => ({
      lineIds: rows.map(checkoutCartLineKey),
    }),
    [rows],
  );

  const form = useForm<CheckoutCartFormValues>({
    values: cartFormValues,
  });

  const applyLineValidationErrors = useCallback(
    (issues: z.ZodIssue[]) => {
      const byIndex = new Map<number, string[]>();
      for (const issue of issues) {
        const path = issue.path;
        if (path.length >= 2 && path[0] === "lineIds") {
          const raw = path[1];
          const idx = typeof raw === "number" ? raw : Number(raw);
          if (!Number.isInteger(idx) || idx < 0) continue;
          const list = byIndex.get(idx) ?? [];
          list.push(issue.message);
          byIndex.set(idx, list);
        }
      }
      for (const [idx, msgs] of byIndex) {
        form.setError(`lineIds.${idx}`, {
          type: "manual",
          message: [...new Set(msgs)].join("\n"),
        });
      }
    },
    [form],
  );

  const handleCheckoutSubmit = useCallback<
    SubmitHandler<CheckoutCartFormValues>
  >(() => {
    rows.forEach((_, i) => {
      form.clearErrors(`lineIds.${i}`);
    });
    const parsed = validationSchema.safeParse({
      lineIds: form.getValues("lineIds"),
    });
    if (!parsed.success) {
      applyLineValidationErrors(parsed.error.issues);
      return;
    }

    const params = new URLSearchParams();
    if (currentPackage?.packageId != null && currentPackage.packageId > 0) {
      params.set("packageId", String(currentPackage.packageId));
    }

    if (params.size === 0) {
      return;
    }
    onSubmitSuccess?.();
    navigate(`/${Paths.PackageManager}?${params.toString()}`);
  }, [
    applyLineValidationErrors,
    form,
    navigate,
    rows,
    currentPackage,
    validationSchema,
    onSubmitSuccess,
  ]);

  const handleRemove = useCallback(
    (pkg: IHubPackage) => {
      const source = currentPackage;
      if (source == null) return;
      const id = pkg.packageId;
      if (id == null || id <= 0 || source.packageId !== id) return;
      const relToRemove = pkg.catalogRelations?.[0];
      if (!relToRemove) return;

      removeFromCart(
        source,
        relToRemove.catalogId ?? 0,
        relToRemove.catalogVersion ?? 0,
      );
    },
    [currentPackage, removeFromCart],
  );

  const handleRemoveSolutionAndAddons = useCallback(
    (solutionPkg: IHubPackage) => {
      const source = currentPackage;
      if (source == null) return;
      const pid = solutionPkg.packageId;
      if (pid == null || pid <= 0 || source.packageId !== pid) return;

      const solutionCatalog = findHubCatalogForPackage(
        catalogsData,
        solutionPkg,
      );
      if (
        solutionCatalog == null ||
        !isSolutionCatalog(solutionCatalog) ||
        solutionCatalog.catalogId == null ||
        solutionCatalog.catalogId <= 0
      ) {
        return;
      }
      const solutionId = solutionCatalog.catalogId;

      const keysToRemove = new Set<string>();
      for (const rowPkg of rows) {
        const c = findHubCatalogForPackage(catalogsData, rowPkg);
        if (c == null) continue;
        const rel = rowPkg.catalogRelations?.[0];
        if (rel == null) continue;
        const rowCatalogId = c.catalogId;
        if (rowCatalogId == null || rowCatalogId <= 0) continue;

        if (isSolutionCatalog(c) && rowCatalogId === solutionId) {
          keysToRemove.add(catalogRelationKey(rel));
          continue;
        }
        if (isAddonCatalog(c)) {
          const parents = c.parentCatalogIds ?? [];
          if (parents.includes(solutionId)) {
            keysToRemove.add(catalogRelationKey(rel));
          }
        }
      }

      const rels = source.catalogRelations ?? [];
      const nextRelations = rels.filter(
        (r) => !keysToRemove.has(catalogRelationKey(r)),
      );

      if (nextRelations.length === rels.length) return;

      const body: IHubPackage = {
        ...source,
        catalogRelations: nextRelations,
      };

      updateHubPackage(body, {
        onSuccess: () => {
          toast.add({
            title: t("hub.checkout.remove_bundle_success", {
              defaultValue: "Removed solution and add-ons from package",
            }),
            data: { type: "success" },
          });
        },
        onError: () => {
          toast.add({
            title: t("hub.checkout.remove_bundle_error", {
              defaultValue: "Could not remove solution bundle",
            }),
            data: { type: "error" },
          });
        },
      });
    },
    [currentPackage, catalogsData, rows, updateHubPackage, t, toast],
  );

  const handleRemoveConfirm = useCallback(() => {
    const state = removeConfirm;
    setRemoveConfirm(null);
    if (state?.mode === "line") handleRemove(state.pkg);
    else if (state?.mode === "bundle")
      handleRemoveSolutionAndAddons(state.solutionPkg);
  }, [removeConfirm, handleRemove, handleRemoveSolutionAndAddons]);

  const orderedCartEntries = useMemo(
    () => getOrderedCartEntries(rows, catalogsData),
    [rows, catalogsData],
  );

  const addonCountBySolutionCatalogId = useMemo(() => {
    const map = new Map<number, number>();
    for (const pkg of rows) {
      const c = findHubCatalogForPackage(catalogsData, pkg);
      if (c == null || !isAddonCatalog(c)) continue;
      for (const parentId of c.parentCatalogIds ?? []) {
        if (parentId > 0) {
          map.set(parentId, (map.get(parentId) ?? 0) + 1);
        }
      }
    }
    return map;
  }, [rows, catalogsData]);

  return (
    <>
      <ConfirmAction
        open={removeConfirm != null}
        onOpenChange={(open) => !open && setRemoveConfirm(null)}
        title={
          removeConfirm?.mode === "bundle"
            ? t("hub.checkout.remove_bundle_confirm_title", {
                defaultValue: "Remove solution and add-ons?",
              })
            : t("hub.checkout.remove_confirm_title", {
                defaultValue: "Remove from package?",
              })
        }
        description={
          removeConfirm?.mode === "bundle"
            ? t("hub.checkout.remove_bundle_confirm_description", {
                name:
                  (
                    removeConfirm.solutionPkg.label ??
                    removeConfirm.solutionPkg.name ??
                    ""
                  ).trim() || "",
                defaultValue:
                  'Remove "{{name}}" and all add-ons for this solution from your package? You can add them again from the catalog later.',
              })
            : t("hub.checkout.remove_confirm_description", {
                name:
                  (removeConfirm?.mode === "line"
                    ? (removeConfirm.pkg.label ?? removeConfirm.pkg.name ?? "")
                    : ""
                  ).trim() || "",
                defaultValue:
                  'Remove "{{name}}" from your package? You can add it again from the catalog later.',
              })
        }
        confirmText={t("hub.checkout.remove", {
          defaultValue: "Remove",
        })}
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant="destructive"
        onConfirm={handleRemoveConfirm}
      />
      <Form {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleCheckoutSubmit)}
          className="flex flex-col gap-3 pr-4">
          {orderedCartEntries.map((entry, orderedIndex) => {
            const { pkg, catalog, group } = entry;
            const catalogTypeBadge = resolveHubCatalogTypeBadge(
              catalog?.catalogTypeLabel,
            );

            const visibilityBadge = catalog?.visibility
              ? resolveHubCatalogVisibilityBadge(catalog.visibility, t)
              : null;

            const headerBadges: IBadgeStripItem[] = [];
            if (catalogTypeBadge) {
              headerBadges.push(catalogTypeBadge);
            }
            if (visibilityBadge) {
              headerBadges.push(visibilityBadge);
            }

            const solutionCatalogId =
              catalog?.catalogId != null && catalog.catalogId > 0
                ? catalog.catalogId
                : undefined;
            const showRemoveSolutionBundle =
              group === 0 &&
              solutionCatalogId != null &&
              (addonCountBySolutionCatalogId.get(solutionCatalogId) ?? 0) > 0;

            const removeLineCatalogLabel = (
              catalog?.label ??
              catalog?.name ??
              pkg.label ??
              pkg.name ??
              ""
            ).trim();

            return (
              <CheckoutCartOrderedFormLine
                isLoading={catalogsLoading}
                key={checkoutCartLineKey(pkg)}
                orderedCartEntries={orderedCartEntries}
                orderedIndex={orderedIndex}
                entry={entry}
                control={form.control}
                nestedAddonsTitle={t("hub.checkout.section_addons", {
                  defaultValue: "Add-ons",
                })}
                otherAddonsTitle={t("hub.checkout.section_other_addons", {
                  defaultValue: "Other Add-ons",
                })}
                formCardContentClassName="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-4"
                renderFormCardContent={() => (
                  <>
                    <FieldContent className="min-w-0 flex-1 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {headerBadges.length > 0 ? (
                          <BadgeStrip items={headerBadges} />
                        ) : null}
                        <Label className="font-medium text-foreground">
                          {pkg.label ||
                            pkg.name ||
                            catalog?.label ||
                            catalog?.name}
                        </Label>
                      </div>
                      {catalog?.version != null ? (
                        <Badge
                          variant="mono"
                          appearance="light"
                          size="sm"
                          className="shrink-0 w-fit">
                          {t("hub.catalog_manager.card_version", {
                            version: String(catalog.version),
                            defaultValue: "Version {{version}}",
                          })}
                        </Badge>
                      ) : null}

                      <BadgeStrip
                        className="mt-1.5 gap-2"
                        items={(catalog?.supportedCloud ?? []).map((cloud) => ({
                          id: cloud,
                          label: cloud,
                          variant: "secondary",
                          size: "md",
                        }))}
                      />
                      <CardDescription
                        className="mt-2.5 line-clamp-2 wrap-break-word"
                        title={
                          plainCatalogDescription(
                            pkg.description || catalog?.description || "",
                          ) || undefined
                        }>
                        {plainCatalogDescription(
                          pkg.description || catalog?.description || "",
                        )}
                      </CardDescription>
                    </FieldContent>
                    <ButtonGroup>
                      {!isReadOnly && showRemoveSolutionBundle ? (
                        <WithToolTip
                          className={cn(isPopover && "z-3000")}
                          tooltipText={t(
                            "hub.checkout.remove_solution_bundle",
                            {
                              label: removeLineCatalogLabel,
                              defaultValue: removeLineCatalogLabel
                                ? "Remove {{label}} and its add-ons"
                                : "Remove solution and its add-ons",
                            },
                          )}>
                          <Button
                            type="button"
                            variant="destructive"
                            appearance="ghost"
                            size="icon"
                            aria-label={t(
                              "hub.checkout.remove_solution_bundle",
                              {
                                label: removeLineCatalogLabel,
                                defaultValue: removeLineCatalogLabel
                                  ? "Remove {{label}} and its add-ons"
                                  : "Remove solution and its add-ons",
                              },
                            )}
                            onClick={() =>
                              setRemoveConfirm({
                                mode: "bundle",
                                solutionPkg: pkg,
                              })
                            }>
                            <PackageX className="size-4" aria-hidden />
                          </Button>
                        </WithToolTip>
                      ) : null}
                      {!isReadOnly && (
                        <WithToolTip
                          className={cn(isPopover && "z-3000")}
                          tooltipText={t(
                            "hub.checkout.remove_line_with_label",
                            {
                              label: removeLineCatalogLabel,
                              defaultValue: removeLineCatalogLabel
                                ? "Remove {{label}}"
                                : "Remove",
                            },
                          )}>
                          <Button
                            type="button"
                            variant="destructive"
                            appearance="ghost"
                            size="icon"
                            aria-label={t(
                              "hub.checkout.remove_line_with_label",
                              {
                                label: removeLineCatalogLabel,
                                defaultValue: removeLineCatalogLabel
                                  ? "Remove {{label}}"
                                  : "Remove",
                              },
                            )}
                            onClick={() =>
                              setRemoveConfirm({ mode: "line", pkg })
                            }>
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        </WithToolTip>
                      )}
                    </ButtonGroup>
                  </>
                )}
              />
            );
          })}
        </form>
      </Form>
    </>
  );
}
